/**
 * @fileoverview Shared EmailImport schema used by both frontend and backend.
 * Queue table for async email-to-note conversion via Haraka SMTP server.
 */

import { z } from 'zod';
import { commonValidators, createCrudSchemas, createEntityIdSchema, createOptionalDatetimeSchema, createdAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { EmailImportStatusEnum, ID_PATTERNS } from './types.js';

/**
 * EmailImport validation schema (queue table)
 */
export const EmailImportSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.EMAIL_IMPORT, 'ID must be in email import nanoid7 format (ei + digit 2-9 + 4 alphanumeric)'),
  account_id: commonValidators.foreignKey('Account ID'),
  message_id: z.string().min(1, 'Message ID is required'),
  from_address: z.string().min(1, 'From address is required'),
  from_name: z.string()
    .transform((val) => (val === '' ? null : val))
    .pipe(z.string().nullable())
    .optional()
    .nullable(),
  subject: z.string()
    .transform((val) => (val === '' ? null : val))
    .pipe(z.string().nullable())
    .optional()
    .nullable(),
  body_text: z.string().optional().nullable(),
  body_html: z.string().optional().nullable(),
  received_at: createOptionalDatetimeSchema('Received at'),
  raw_headers: commonValidators.jsonField('Raw headers'),
  attachments: commonValidators.jsonField('Attachments'),
  status: EmailImportStatusEnum.default('pending'),
  note_id: commonValidators.noteForeignKey('Note ID'),
  error_message: z.string()
    .transform((val) => (val === '' ? null : val))
    .pipe(z.string().nullable())
    .optional()
    .nullable(),
  retry_count: z.number()
    .int('Retry count must be an integer')
    .min(0, 'Retry count cannot be negative')
    .max(10, 'Retry count cannot exceed 10')
    .default(0),
  processing_started_at: createOptionalDatetimeSchema('Processing started at'),
  completed_at: createOptionalDatetimeSchema('Completed at'),
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

const { CreateSchema } = createCrudSchemas(EmailImportSchema, {
  additionalOmit: ['status', 'note_id', 'error_message', 'retry_count', 'processing_started_at', 'completed_at'],
});

const EmailImportUpdateBase = EmailImportSchema.omit({ id: true, created_at: true });

export const EmailImportCreateSchema = CreateSchema;
export const EmailImportUpdateSchema = EmailImportUpdateBase.partial();

export type EmailImport = z.infer<typeof EmailImportSchema>;
export type EmailImportCreate = z.infer<typeof EmailImportCreateSchema>;
export type EmailImportUpdate = z.infer<typeof EmailImportUpdateSchema>;

export const EMAIL_IMPORT_FIELDS = getSchemaFields(EmailImportSchema);
